import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CompanyResource } from '../types';
import api from '../services/api';
import { SearchAndFilters, CompanyList, Pagination, LoadingSpinner } from '../components';

const Companies: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [companies, setCompanies] = useState<CompanyResource[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Get values directly from URL search params
  const currentPage = parseInt(searchParams.get('page') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');
  const search = searchParams.get('search') || '';
  const showNeedFollowUp = searchParams.get('isNeedFollowUp') === '1';
  const isActivelyUsing = searchParams.get('isActivelyUsing') === '1';
  const orderBy = searchParams.get('orderBy') || 'created-at';
  const marketingChannelId = searchParams.get('marketingChannelId') || '';
  const marketingChannelName = searchParams.get('marketingChannelName') || '';
  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';
  const provinsi = searchParams.get('provinsi') || '';
  const kabupaten = searchParams.get('kabupaten') || '';

  // Local state only for UI controls that need debouncing or immediate feedback
  const [searchInput, setSearchInput] = useState(search);
  const [total, setTotal] = useState(0);

  // Load data when URL params change (single dependency)
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoading(true);
        const response = await api.getCompanies(currentPage, perPage, search, showNeedFollowUp, orderBy, {
          marketingChannelId: marketingChannelId || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          provinsi: provinsi || undefined,
          kabupaten: kabupaten || undefined,
          isActivelyUsing: isActivelyUsing || undefined,
        });
        setCompanies(response.data);
        setTotal(response.meta.total);
      } catch (error) {
        console.error('Error loading companies:', error);
        setCompanies([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
  }, [currentPage, perPage, search, showNeedFollowUp, orderBy, marketingChannelId, startDate, endDate, provinsi, kabupaten, isActivelyUsing]);

  // Debounce search input and update URL
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== search) {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          if (searchInput) {
            newParams.set('search', searchInput);
          } else {
            newParams.delete('search');
          }
          newParams.set('page', '1'); // Reset to first page
          return newParams;
        }, { replace: true });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, search, setSearchParams]);

  // Update URL when filters change (skip if already matches)
  useEffect(() => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      let hasChanges = false;
      
      if (currentPage !== parseInt(prev.get('page') || '1')) {
        newParams.set('page', currentPage.toString());
        hasChanges = true;
      }
      if (perPage !== parseInt(prev.get('perPage') || '10')) {
        newParams.set('perPage', perPage.toString());
        hasChanges = true;
      }
      if (showNeedFollowUp !== (prev.get('isNeedFollowUp') === '1')) {
        if (showNeedFollowUp) {
          newParams.set('isNeedFollowUp', '1');
        } else {
          newParams.delete('isNeedFollowUp');
        }
        hasChanges = true;
      }
      if (orderBy !== (prev.get('orderBy') || 'created-at')) {
        newParams.set('orderBy', orderBy);
        hasChanges = true;
      }
      
      return hasChanges ? newParams : prev;
    }, { replace: true });
  }, [currentPage, perPage, showNeedFollowUp, orderBy, setSearchParams]);

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    }, { replace: true });
  };

  const handlePerPageChange = (newPerPage: number) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('perPage', newPerPage.toString());
      newParams.set('page', '1');
      return newParams;
    }, { replace: true });
  };

  const handleShowNeedFollowUpChange = (checked: boolean) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (checked) {
        newParams.set('isNeedFollowUp', '1');
      } else {
        newParams.delete('isNeedFollowUp');
      }
      newParams.set('page', '1'); // Reset to first page
      return newParams;
    }, { replace: true });
  };

  const handleIsActivelyUsingChange = (checked: boolean) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (checked) {
        newParams.set('isActivelyUsing', '1');
      } else {
        newParams.delete('isActivelyUsing');
      }
      newParams.set('page', '1'); // Reset to first page
      return newParams;
    }, { replace: true });
  };

  const handleOrderByChange = (value: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('orderBy', value);
      newParams.set('page', '1'); // Reset to first page
      return newParams;
    }, { replace: true });
  };

  if (loading && companies.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading companies..." />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage and monitor all companies in the system
        </p>
      </div>

      <SearchAndFilters
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        perPage={perPage}
        onPerPageChange={handlePerPageChange}
        total={total}
        loading={loading}
        showNeedFollowUp={showNeedFollowUp}
        onShowNeedFollowUpChange={handleShowNeedFollowUpChange}
        isActivelyUsing={isActivelyUsing}
        onIsActivelyUsingChange={handleIsActivelyUsingChange}
        orderBy={orderBy}
        onOrderByChange={handleOrderByChange}
      />

      {marketingChannelId && (
        <div className="mb-4 flex items-center gap-2">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200 text-sm text-primary-700">
            Channel:&nbsp;
            <strong>
              {marketingChannelName ||
                (marketingChannelId === 'other'
                  ? 'Lainnya'
                  : marketingChannelId === 'none'
                  ? 'Tidak diisi'
                  : companies[0]?.marketingChannel?.name || 'Selected channel')}
            </strong>
            {(startDate || endDate) && (
              <span className="text-primary-500">
                &middot; {startDate || '…'} – {endDate || '…'}
              </span>
            )}
            <button
              onClick={() => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete('marketingChannelId');
                  next.delete('marketingChannelName');
                  next.delete('startDate');
                  next.delete('endDate');
                  next.set('page', '1');
                  return next;
                }, { replace: true });
              }}
              className="ml-1 text-primary-500 hover:text-primary-700"
              aria-label="Clear channel filter"
            >
              ✕
            </button>
          </span>
        </div>
      )}

      {(provinsi || kabupaten) && (
        <div className="mb-4 flex items-center gap-2">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200 text-sm text-primary-700">
            Area:&nbsp;<strong>{kabupaten ? `${kabupaten}, ${provinsi}` : provinsi}</strong>
            <button
              onClick={() => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete('provinsi');
                  next.delete('kabupaten');
                  next.set('page', '1');
                  return next;
                }, { replace: true });
              }}
              className="ml-1 text-primary-500 hover:text-primary-700"
              aria-label="Clear area filter"
            >
              ✕
            </button>
          </span>
        </div>
      )}

      <CompanyList
        companies={companies}
        loading={loading}
        search={search}
      />

      <Pagination
        currentPage={currentPage}
        total={total}
        perPage={perPage}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default Companies;
